import { ExternalOperationType } from "amnPos/shiftsAndExternals/externalOperations/ExternalOperationData";
import { employeeSettings } from "amnPos/slices/EmployeeSettingSlice";
import { useAppSelector } from "app/hooks";
import {
  PermissionErrorType,
  IPermissionResponse,
  PermissionType,
} from "./IPermissions";

export default function usePermissions() {
  const employeeSettings = useAppSelector((store) => store.employeeSettings);

  function canEmployeeSaveExtOp(
    extOpType: ExternalOperationType,
    maxAllowedValueTransaction: number,
    usedFC: boolean,
    changedExchangeFactor: boolean,
  ): IPermissionResponse {
    if (
      employeeSettings.transactionsOptions.isHavingMaxValueForTransaction &&
      employeeSettings.transactionsOptions.maxAllowedValueTransaction <
      maxAllowedValueTransaction
    ) {
      return {
        response: false,
        error: PermissionErrorType.ExceededAllowedMaxTransactionValue,
      };
    }

    const permissionMap: Partial<
      Record<
        ExternalOperationType,
        { hasPermission: boolean; errorType: PermissionErrorType }
      >
    > = {
      [ExternalOperationType.Credit]: {
        hasPermission: employeeSettings.transactionsOptions.canPayToClient,
        errorType: PermissionErrorType.CantPayToClient,
      },
      [ExternalOperationType.Debit]: {
        hasPermission: employeeSettings.transactionsOptions.canReciveFromClient,
        errorType: PermissionErrorType.CantReceiveFromClient,
      },
      [ExternalOperationType.PayToCentralCash]: {
        hasPermission: employeeSettings.transactionsOptions.canPayToCenteralBox,
        errorType: PermissionErrorType.CantPayToCentralBox,
      },
      [ExternalOperationType.ReceiveFromCentralCash]: {
        hasPermission:
          employeeSettings.transactionsOptions.canReciveFromCenteralBox,
        errorType: PermissionErrorType.CantReceiveFromCentralBox,
      },
    };

    const permission = permissionMap[extOpType];

    if (!permission) {
      return { response: false, error: PermissionErrorType.UnknownError };
    }

    return {
      response: permission.hasPermission,
      error: permission.hasPermission
        ? PermissionErrorType.NoError
        : permission.errorType,
    };
  }

  function canEmployeeDeleteTransaction(): IPermissionResponse {
    return {
      response:
        employeeSettings.transactionsOptions.canDeleteExternalTransacation,
      error: employeeSettings.transactionsOptions.canDeleteExternalTransacation
        ? PermissionErrorType.NoError
        : PermissionErrorType.CantDeleteExternalTransaction,
    };
  }
  function canEmployee(type: PermissionType): IPermissionResponse {
    const permissionMap: Partial<
      Record<
        PermissionType,
        { hasPermission: boolean; errorType: PermissionErrorType }
      >
    > = {
      [PermissionType.ChangePrice]: {
        hasPermission:
          employeeSettings.transactionPermissionsOptions.changePrice,
        errorType: PermissionErrorType.CantChangePrice,
      },
      [PermissionType.SellInDebt]: {
        hasPermission:
          employeeSettings.transactionPermissionsOptions.sellInDebt,
        errorType: PermissionErrorType.CantSellInDebt,
      },
      [PermissionType.DeleteAnItem]: {
        hasPermission:
          employeeSettings.transactionPermissionsOptions.deleteAnItem,
        errorType: PermissionErrorType.CantDeleteAnItem,
      },
      [PermissionType.CanDeleteExternalOperation]: {
        hasPermission:
          employeeSettings.transactionsOptions.canDeleteExternalTransacation,
        errorType: PermissionErrorType.CantDeleteExternalTransaction,
      },
      [PermissionType.ReduceQuantity]: {
        hasPermission:
          employeeSettings.transactionPermissionsOptions.reduceQuantity,
        errorType: PermissionErrorType.CantReduceQuantity,
      },
      [PermissionType.ViewSettledTransactions]: {
        hasPermission:
          employeeSettings.transactionPermissionsOptions
            .viewSettledTransactions,
        errorType: PermissionErrorType.CantViewSettledTransactions,
      },
      [PermissionType.ReprintSettledTransaction]: {
        hasPermission:
          employeeSettings.transactionPermissionsOptions
            .reprintSettledTransaction,
        errorType: PermissionErrorType.CantReprintSettledTransaction,
      },
      [PermissionType.OpenDrawerManualy]: {
        hasPermission:
          employeeSettings.transactionPermissionsOptions.openDrawerManualy,
        errorType: PermissionErrorType.CantOpenDrawerManually,
      },
      [PermissionType.MakeDiscountOnItem]: {
        hasPermission:
          employeeSettings.transactionPermissionsOptions.makeDiscountOnItem,
        errorType: PermissionErrorType.CantMakeDiscountOnItem,
      },
      [PermissionType.MakeDiscountOnTransaction]: {
        hasPermission:
          employeeSettings.transactionPermissionsOptions
            .makeDiscountOnTransaction,
        errorType: PermissionErrorType.CantMakeDiscountOnTransaction,
      },
      [PermissionType.CanUseFc]: {
        hasPermission: employeeSettings.transactionPermissionsOptions.canUseFc,
        errorType: PermissionErrorType.CantUseFc,
      },
      [PermissionType.CanModifyExChangeRates]: {
        hasPermission:
          employeeSettings.transactionPermissionsOptions.canModifyExChangeRates,
        errorType: PermissionErrorType.CantModifyExchangeRates,
      },
      [PermissionType.CanPrintCustomizationBeforeCollect]: {
        hasPermission:
          employeeSettings.transactionPermissionsOptions.printCustomizationBeforeCollect,
        errorType: PermissionErrorType.CantPrintCustomizationBeforeCollect,
      },
      [PermissionType.SendEReceipt]: {
        hasPermission:
          employeeSettings.transactionPermissionsOptions.sendEReceipt,
        errorType: PermissionErrorType.CantSendEReceipt,
      },
      [PermissionType.CanCloseShift]: {
        hasPermission: employeeSettings.shiftOptions.closeShift,
        errorType: PermissionErrorType.CantCloseShift,
      },
      [PermissionType.CanViewCalculatedCash]: {
        hasPermission: employeeSettings.shiftOptions.viewCalculatedCash,
        errorType: PermissionErrorType.CantViewCalculatedCash,
      },
      [PermissionType.CanPrintShiftSummary]: {
        hasPermission: employeeSettings.shiftOptions.printShiftSummary,
        errorType: PermissionErrorType.CantPrintShiftSummary,
      },
      [PermissionType.CanPostCashToNextShift]: {
        hasPermission: employeeSettings.shiftOptions.postCashToNextShift,
        errorType: PermissionErrorType.CantPostCashToNextShift,
      },
      [PermissionType.CanUnCloseShift]: {
        hasPermission: employeeSettings.shiftOptions.unCloseShift,
        errorType: PermissionErrorType.CantUnCloseShift,
      },
      [PermissionType.CanChangeCentralBoxReceiptNumberMandatory]: {
        hasPermission:
          employeeSettings.shiftOptions.centralBoxReceiptNumberMandatory,
        errorType:
          PermissionErrorType.CantChangeCentralBoxReceiptNumberMandatory,
      },
      [PermissionType.CanViewCustomerBalanceStatement]: {
        hasPermission: employeeSettings.reportsOptions.customerBalanceStatement,
        errorType: PermissionErrorType.CantViewCustomerBalanceStatement,
      },
      [PermissionType.CanShowCustomerBalance]: {
        hasPermission: employeeSettings.reportsOptions.showCustomerBalance,
        errorType: PermissionErrorType.CantShowCustomerBalance,
      },
      [PermissionType.CanViewCalculatedCashDetails]: {
        hasPermission: employeeSettings.reportsOptions.calculatedCashDetails,
        errorType: PermissionErrorType.CantViewCalculatedCashDetails,
      },
      [PermissionType.CanViewCalculatedNonCashDetails]: {
        hasPermission: employeeSettings.reportsOptions.calculatedNonCashDetails,
        errorType: PermissionErrorType.CantViewCalculatedNonCashDetails,
      },
      [PermissionType.CanViewShiftDigest]: {
        hasPermission: employeeSettings.reportsOptions.shiftDigest,
        errorType: PermissionErrorType.CantViewShiftDigest,
      },
      [PermissionType.CanViewMaterialsAndServicesTransactionsDetail]: {
        hasPermission:
          employeeSettings.reportsOptions
            .materialsAndServicesTransactionsDetail,
        errorType:
          PermissionErrorType.CantViewMaterialsAndServicesTransactionsDetail,
      },
      [PermissionType.CanViewExternalTransactionsDetails]: {
        hasPermission:
          employeeSettings.reportsOptions.externalTransactionsDetails,
        errorType: PermissionErrorType.CantViewExternalTransactionsDetails,
      },
      [PermissionType.CanViewClosingDetails]: {
        hasPermission: employeeSettings.reportsOptions.closingDetails,
        errorType: PermissionErrorType.CantViewClosingDetails,
      },
      // Order
      [PermissionType.CanAddPickUpOrder]: {
        hasPermission: employeeSettings.orderOptions.addPickUpOrder,
        errorType: PermissionErrorType.CantAddPickUpOrder,
      },
      [PermissionType.CanAddDeliveryOrder]: {
        hasPermission: employeeSettings.orderOptions.addDeliveryOrder,
        errorType: PermissionErrorType.CantAddDeliveryOrder,
      },
      [PermissionType.CanEditDeliveryFees]: {
        hasPermission: employeeSettings.orderOptions.editDeliveryFees,
        errorType: PermissionErrorType.CantEditDeliveryFees,
      },
      [PermissionType.CanReceivePrepayment]: {
        hasPermission: employeeSettings.orderOptions.receivePrepayment,
        errorType: PermissionErrorType.CantReceivePrepayment,
      },
      [PermissionType.CanCancelOrder]: {
        hasPermission: employeeSettings.orderOptions.cancelOrder,
        errorType: PermissionErrorType.CantCancelOrder,
      },
      [PermissionType.CanEditSavedOrder]: {
        hasPermission: employeeSettings.orderOptions.editSavedOrder,
        errorType: PermissionErrorType.CantEditSavedOrder,
      },
      [PermissionType.CanUseOrderManagementTab]: {
        hasPermission: employeeSettings.orderOptions.useOrderManagementTab,
        errorType: PermissionErrorType.CantUseOrderManagementTab,
      },
      [PermissionType.CanUseDeliveryOrdersManagement]: {
        hasPermission:
          employeeSettings.orderOptions.useDeliveryOrdersManagement,
        errorType: PermissionErrorType.CantUseDeliveryOrdersManagement,
      },
      [PermissionType.CanSettlePickupOrder]: {
        hasPermission: employeeSettings.orderOptions.settlePickupOrder,
        errorType: PermissionErrorType.CantSettlePickupOrder,
      },
      [PermissionType.CanSettleDeliveryTrip]: {
        hasPermission: employeeSettings.orderOptions.settleDeliveryTrip,
        errorType: PermissionErrorType.CantSettleDeliveryTrip,
      },
      [PermissionType.CanHandleTableOrder]: {
        hasPermission: employeeSettings.orderOptions.canHandleTableOrder,
        errorType: PermissionErrorType.CantHandleTableOrder,
      },
      [PermissionType.CanSettleTableOrder]: {
        hasPermission: employeeSettings.orderOptions.settleTableOrder,
        errorType: PermissionErrorType.CantSettleTableOrder,
      },
      [PermissionType.CanMoveOrder]: {
        hasPermission: employeeSettings.orderOptions.moveOrder,
        errorType: PermissionErrorType.CantMoveOrder,
      },
      [PermissionType.CanMoveOrderItems]: {
        hasPermission: employeeSettings.orderOptions.moveOrderItems,
        errorType: PermissionErrorType.CantMoveOrderItems,
      },
      // Client
      [PermissionType.CanAddNewClient]: {
        hasPermission: employeeSettings.clientOptions.canAddNewClient,
        errorType: PermissionErrorType.CantAddNewClient,
      },
      [PermissionType.CanModifyExistingClient]: {
        hasPermission: employeeSettings.clientOptions.canModifyExistingClient,
        errorType: PermissionErrorType.CantModifyExistingClient,
      },
      [PermissionType.CanChangeDefaultCountryAndProvince]: {
        hasPermission:
          employeeSettings.clientOptions.canChangeDefaultCountryAndProvince,
        errorType: PermissionErrorType.CantChangeDefaultCountryAndProvince,
      },
      [PermissionType.CanAddNewAddress]: {
        hasPermission: employeeSettings.clientOptions.canAddNewAddress,
        errorType: PermissionErrorType.CantAddNewAddress,
      },
      [PermissionType.CanModifyAddress]: {
        hasPermission: employeeSettings.clientOptions.canModifyAddress,
        errorType: PermissionErrorType.CantModifyAddress,
      },
      // Menus
      [PermissionType.CanChangeMenu]: {
        hasPermission: employeeSettings.menusOptions.canChangeMenu,
        errorType: PermissionErrorType.CantChangeMenu,
      },
      [PermissionType.CanChangeMenuWhenOpenOrder]: {
        hasPermission: employeeSettings.menusOptions.canChangeMenuWhenOpenOrder,
        errorType: PermissionErrorType.CantChangeMenuWhenOpenOrder,
      },
      // PreparingAndServing
      [PermissionType.CanCancelOrderAfterPreparationStarts]: {
        hasPermission:
          employeeSettings.preparingAndServing
            .canCancelOrderAfterPreparationStarts,
        errorType: PermissionErrorType.CantCancelOrderAfterPreparationStarts,
      },
      [PermissionType.CanCancelOrderAfterServed]: {
        hasPermission:
          employeeSettings.preparingAndServing.canCancelOrderAfterServed,
        errorType: PermissionErrorType.CantCancelOrderAfterServed,
      },
      [PermissionType.CanCancelItemAfterPreparationStarts]: {
        hasPermission:
          employeeSettings.preparingAndServing
            .canCancelItemAfterPreparationStarts,
        errorType: PermissionErrorType.CantCancelItemAfterPreparationStarts,
      },
      [PermissionType.CanCancelItemAfterServed]: {
        hasPermission:
          employeeSettings.preparingAndServing.canCancelItemAfterServed,
        errorType: PermissionErrorType.CantCancelItemAfterServed,
      },
      [PermissionType.CanChangeItemQuantityAfterPreparationStarts]: {
        hasPermission:
          employeeSettings.preparingAndServing
            .canChangeItemQuantityAfterPreparationStarts,
        errorType:
          PermissionErrorType.CantChangeItemQuantityAfterPreparationStarts,
      },
      [PermissionType.CanChangeItemQuantityAfterServed]: {
        hasPermission:
          employeeSettings.preparingAndServing.canChangeItemQuantityAfterServed,
        errorType: PermissionErrorType.CantChangeItemQuantityAfterServed,
      },
      [PermissionType.CanReturnOrderFromInPreparation]: {
        hasPermission:
          employeeSettings.preparingAndServing.canReturnOrderFromInPreparation,
        errorType: PermissionErrorType.CantReturnOrderFromInPreparation,
      },
      [PermissionType.CanEndPreparationInKitchenWindow]: {
        hasPermission:
          employeeSettings.preparingAndServing.canEndPreparationInKitchenWindow,
        errorType: PermissionErrorType.CantEndPreparationInKitchenWindow,
      },
      [PermissionType.CanPayToClient]: {
        hasPermission: employeeSettings.transactionsOptions.canPayToClient,
        errorType: PermissionErrorType.CantPayToClient,
      },
      [PermissionType.CanReceiveFromClient]: {
        hasPermission: employeeSettings.transactionsOptions.canReciveFromClient,
        errorType: PermissionErrorType.CantReceiveFromClient,
      },
      [PermissionType.CanPayToCentralBox]: {
        hasPermission: employeeSettings.transactionsOptions.canPayToCenteralBox,
        errorType: PermissionErrorType.CantPayToCentralBox,
      },
      [PermissionType.CanReceiveFromCentralBox]: {
        hasPermission:
          employeeSettings.transactionsOptions.canReciveFromCenteralBox,
        errorType: PermissionErrorType.CantReceiveFromCentralBox,
      },
    };
    const permission = permissionMap[type];

    if (!permission) {
      return { response: false, error: PermissionErrorType.UnknownError };
    }

    return {
      response: permission.hasPermission,
      error: permission.hasPermission
        ? PermissionErrorType.NoError
        : permission.errorType,
    };
  }

  return { canEmployeeSaveExtOp, canEmployeeDeleteTransaction, canEmployee };
}
